import { useCallback } from 'react';

import type {
  AnchorInfo,
  CodeBlockRedlineItem,
  DocumentSigningLockState,
  ParagraphStateReader,
  ProposalSuggestion,
  UUID,
} from '@aline/common';
import {
  createMarkdownLocationLink,
  extractBlockAnchorsFromAlineDoc,
  extractParagraphsFromAlineDoc,
  formatAnchorId,
  materializeHtmlEdit,
} from '@aline/common';
import { useCurrentUserId } from 'src/gql';
import { useDocumentSignatureStatus } from 'src/hooks';
import { useMaybeEditorForDocument } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

import { getIsSuggestionStale } from './playbook-staleness';

/**
 * The version stamped into ParagraphInfo built from the live editor. Cosmetic:
 * it only feeds the location links on cards this hook discards — the effect
 * that matters is the editor mutation.
 */
const LIVE_VERSION_ID = 'live';

/**
 * Builds the applier's anchor lookup from the live editor's current content —
 * the browser-side equivalent of the server's `createParagraphState` seeding,
 * built fresh per materialization so the anchors reflect the document as it
 * stands at the click.
 */
function buildLiveParagraphState(
  editor: NonNullable<ReturnType<typeof useMaybeEditorForDocument>>,
  documentId: UUID
): { anchorInfoByKey: Map<string, AnchorInfo>; paragraphState: ParagraphStateReader } {
  const docParts = editor.children;
  const anchorInfoByKey = new Map<string, AnchorInfo>();

  for (const paragraph of extractParagraphsFromAlineDoc(docParts)) {
    anchorInfoByKey.set(paragraph.locationAnchor, {
      cellRanges: paragraph.cellRanges,
      documentId,
      documentName: '',
      fullText: paragraph.segments.map(segment => segment.text).join(''),
      kind: 'paragraph',
      listLevel: paragraph.listLevel,
      listMarker: paragraph.listMarker,
      locationAnchor: paragraph.locationAnchor,
      locationLink: createMarkdownLocationLink(
        documentId,
        LIVE_VERSION_ID,
        paragraph.locationAnchor
      ),
      section: paragraph.section,
      segments: paragraph.segments,
      text: paragraph.segments
        .filter(segment => segment.edit !== 'remove')
        .map(segment => segment.text)
        .join(''),
      type: paragraph.type,
      versionId: LIVE_VERSION_ID,
    });
  }
  for (const blockAnchor of extractBlockAnchorsFromAlineDoc(docParts)) {
    anchorInfoByKey.set(formatAnchorId(blockAnchor.kind, blockAnchor.locationAnchor), {
      documentId,
      kind: blockAnchor.kind,
      locationAnchor: blockAnchor.locationAnchor,
      pdfPreviewHtml: blockAnchor.pdfPreviewHtml,
      versionId: LIVE_VERSION_ID,
    });
  }

  const paragraphState: ParagraphStateReader = {
    getAllLocationAnchorsForDocument: () => [...anchorInfoByKey.keys()],
    getBlockAnchorInfoByAnchorIdWithLazyLoad: async (_documentId, anchorId) => {
      const info = anchorInfoByKey.get(anchorId);
      return info === undefined || info.kind === 'paragraph' ? undefined : info;
    },
    getParagraphInfoByLocationAnchorWithLazyLoad: async (_documentId, locationAnchor) => {
      const info = anchorInfoByKey.get(locationAnchor);
      return info?.kind === 'paragraph' ? info : undefined;
    },
  };

  return { anchorInfoByKey, paragraphState };
}

/**
 * The browser's answer to the applier's signing-lock question, from what the
 * client already knows about the document. A signed document outranks a sent
 * one: the applier only needs to know the edit must not land, the wording is
 * what differs.
 */
function getLiveSigningLockState(status: {
  isSigned: boolean;
  wasSentForSignature: boolean;
}): DocumentSigningLockState | null {
  if (status.isSigned) return 'signed';
  if (status.wasSentForSignature) return 'sent-for-signature';
  return null;
}

export type MaterializeOutcome =
  | { errorCount: number; type: 'materialized' }
  | { type: 'not-ready' }
  | { type: 'signing-locked' }
  | { type: 'stale' };

/**
 * Materializes an accepted suggestion into the live editor, per ADR 0001 — in
 * the browser, against the document the user is looking at.
 *
 * `acceptSuggestion` is the review accept (ADR 0006). The reviewer already
 * decided in the panel, so the edits land as the reviewer's own tracked
 * changes (glossary: Keep) from the start: `makeSource` declines to stamp, so
 * nothing carries a `source` and no suggestion markers are inserted — the
 * document ends up exactly as if the user had made the edits by hand.
 *
 * Staleness is re-checked here, against the document as it stands at the
 * click: materializing edits generated against text that has since changed
 * writes wrong suggestions into a legal document.
 *
 * A signing-locked document is refused outright rather than left to the
 * applier's chat-only downgrade: with nowhere to show the unapplied edits, an
 * accept that recorded a review against an untouched document would read as a
 * change that landed.
 */
export function usePlaybookMaterialize({ documentId }: { documentId: UUID }) {
  const authorId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const editor = useMaybeEditorForDocument(documentId);
  const { isSigned, wasSentForSignature } = useDocumentSignatureStatus(documentId);

  const acceptSuggestion = useCallback(
    async ({ suggestion }: { suggestion: ProposalSuggestion }): Promise<MaterializeOutcome> => {
      if (!editor || !authorId || !editingMode) return { type: 'not-ready' };

      const signingLockState = getLiveSigningLockState({ isSigned, wasSentForSignature });
      if (signingLockState) return { type: 'signing-locked' };

      const { anchorInfoByKey, paragraphState } = buildLiveParagraphState(editor, documentId);
      if (getIsSuggestionStale(suggestion.previewCards, anchorInfoByKey)) return { type: 'stale' };

      const cards: CodeBlockRedlineItem[] = await materializeHtmlEdit(suggestion.htmlEdit, {
        editorPool: {
          getOrCreateEditor: async () => editor,
          getSigningLockState: async () => signingLockState,
        },
        errorReporter: {
          captureException: (error, context) => console.error(error, context),
          captureMessage: (message, context) => console.error(message, context),
        },
        logger: {
          error: (message, meta) => console.error(message, meta),
          warn: message => console.warn(message),
        },
        makeSource: () => undefined,
        paragraphState,
        userId: authorId,
      });

      const errorCount = cards.filter(card => card.type === 'redline-edit-error').length;
      return { errorCount, type: 'materialized' };
    },
    [authorId, documentId, editingMode, editor, isSigned, wasSentForSignature]
  );

  return { acceptSuggestion };
}
